import i18n from '@/i18n/i18n';

export type DatePrecision = 'year' | 'month' | 'day';

function naLabel(): string {
  return i18n.t('forms:na');
}

function localeTag(): string {
  return i18n.language || 'en';
}

/**
 * Parses a date-only string (YYYY-MM-DD or YYYY-MM-DDTHH:mm...) as local date.
 * Avoids UTC interpretation of "YYYY-MM-DD" which shifts the calendar day in western timezones.
 * @returns { year, month, day } or null if invalid
 */
export function parseDateOnly(dateString: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return { year, month, day };
}

/**
 * Formats a date string to a human-readable date format (date only, no time)
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string (e.g., "January 15, 2024") or "N/A" if invalid
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return naLabel();
  const p = parseDateOnly(dateString);
  if (!p) return naLabel();
  const date = new Date(p.year, p.month - 1, p.day);
  return date.toLocaleDateString(localeTag(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats a temporal value (date) according to precision.
 * Use for birth/death dates where only year, month, or full date may be known.
 * @param dateString - ISO date string (YYYY-MM-DD) or null/undefined
 * @param precision - 'year' | 'month' | 'day'; defaults to 'day'
 * @returns Formatted string (e.g. "1950", "June 1950", "June 15, 1950") or "N/A" if invalid
 */
export function formatTemporal(dateString: string | null | undefined, precision: DatePrecision = 'day'): string {
  if (!dateString) return naLabel();
  const p = parseDateOnly(dateString);
  if (!p) return naLabel();
  if (precision === 'year') return String(p.year);
  const date = new Date(p.year, p.month - 1, p.day);
  if (precision === 'month') {
    return date.toLocaleDateString(localeTag(), { year: 'numeric', month: 'long' });
  }
  return date.toLocaleDateString(localeTag(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats a date string to a human-readable date and time format
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date and time string or "Unknown" if invalid
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return i18n.t('common:unknown');
  try {
    const date = new Date(dateString);
    return date.toLocaleString(localeTag(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: undefined // Let the locale determine 12/24 hour format
    });
  } catch {
    return dateString;
  }
}

/**
 * Parse a date string that may be YYYY, YYYY-MM, or YYYY-MM-DD (or ISO with time) into { year, month, day } for validation.
 * Returns null if invalid.
 */
function parseDatePartsLenient(dateString: string): { year: number; month: number; day: number } | null {
  const s = (dateString || '').trim();
  if (!s) return null;
  // Accept ISO date-time (e.g. 2010-06-01T00:00:00.000Z) by using first 10 chars for full date
  const datePart = s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  const fullMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (fullMatch) {
    const year = parseInt(fullMatch[1], 10);
    const month = parseInt(fullMatch[2], 10);
    const day = parseInt(fullMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const d = new Date(year, month - 1, day);
      if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) return { year, month, day };
    }
    return null;
  }
  const monthMatch = /^(\d{4})-(\d{1,2})$/.exec(s);
  if (monthMatch) {
    const year = parseInt(monthMatch[1], 10);
    const month = parseInt(monthMatch[2], 10);
    if (year >= 1 && year <= 9999 && month >= 1 && month <= 12) return { year, month, day: 1 };
    return null;
  }
  const yearMatch = /^(\d{1,4})$/.exec(s);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    if (year >= 1 && year <= 9999) return { year, month: 1, day: 1 };
  }
  return null;
}

/**
 * Calendar year from stored birth/death (YYYY-MM-DD, ISO datetime with date prefix, YYYY-MM, or YYYY).
 * Do not use `new Date(dateOnlyString).getFullYear()` — that parses date-only strings as UTC and can
 * show the previous year in timezones west of UTC (e.g. Jan 1 → Dec 31 locally).
 */
export function calendarYearFromStoredDate(dateString: string | null | undefined): number | null {
  const s = dateString?.trim();
  if (!s) return null;
  const p = parseDateOnly(s) ?? parseDatePartsLenient(s);
  return p?.year ?? null;
}

/**
 * Midnight in the local timezone for the stored calendar date (same rules as {@link formatTemporal}).
 * Use for age/duration instead of `new Date(storedDateString)`.
 */
export function localCalendarDateFromStored(dateString: string | null | undefined): Date | null {
  const s = dateString?.trim();
  if (!s) return null;
  const p = parseDateOnly(s) ?? parseDatePartsLenient(s);
  if (!p) return null;
  const d = new Date(p.year, p.month - 1, p.day);
  if (d.getFullYear() !== p.year || d.getMonth() !== p.month - 1 || d.getDate() !== p.day) return null;
  return d;
}

/**
 * Format a date string as YYYY-MM-DD for use in date inputs.
 * Accepts Date objects, YYYY-MM-DD, ISO date-time (YYYY-MM-DDTHH...), YYYY-MM, or YYYY (year only).
 * Used by person add/edit forms.
 */
export function formatDateForInput(dateString?: string | Date): string {
  if (dateString == null) return '';
  if (dateString instanceof Date) return formatDateForInput(dateString.toISOString().slice(0, 10));
  const s = String(dateString).trim();
  if (!s) return '';
  const p = parseDatePartsLenient(s);
  if (!p) return '';
  const m = String(p.month).padStart(2, '0');
  const d = String(p.day).padStart(2, '0');
  return `${p.year}-${m}-${d}`;
}

/**
 * Value to show in birth date input based on precision (year only, month, or full date).
 * Used by person add/edit forms.
 */
export function birthDateDisplayValue(birthDate: string, precision: DatePrecision): string {
  if (!birthDate) return '';
  const p = parseDateOnly(birthDate);
  if (!p) return '';
  if (precision === 'year') return String(p.year);
  if (precision === 'month') return `${p.year}-${String(p.month).padStart(2, '0')}`;
  return formatDateForInput(birthDate);
}

/**
 * Normalize raw birth date input to canonical YYYY-MM-DD based on precision.
 * Accepts YYYY-MM-DD (pass-through), YYYY-MM, or YYYY so that DatePartsInput's
 * full-date output is accepted when precision is year or month.
 */
export function toCanonicalBirthDate(raw: string, precision: DatePrecision): string {
  const s = raw?.trim() || '';
  if (!s) return '';
  if (precision === 'year') {
    const fullMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (fullMatch) return s;
    const y = parseInt(s, 10);
    if (y >= 1 && y <= 9999) return `${y}-01-01`;
    return '';
  }
  if (precision === 'month') {
    const fullMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (fullMatch) return s;
    const match = /^(\d{4})-(\d{2})$/.exec(s);
    if (match) return `${match[1]}-${match[2]}-01`;
    return '';
  }
  return formatDateForInput(s);
}

/**
 * Parse YYYY, YYYY-MM, or YYYY-MM-DD into separate year, month, day strings for part-by-part editing.
 * Used when precision varies (year only, month+year, or full date). Returns empty strings for
 * missing/invalid parts.
 */
export function parseDateToParts(dateString: string): { year: string; month: string; day: string } {
  const s = (dateString || '').trim();
  if (!s) return { year: '', month: '', day: '' };
  const fullMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (fullMatch) {
    const p = parseDateOnly(s);
    if (!p) return { year: '', month: '', day: '' };
    return {
      year: String(p.year),
      month: String(p.month).padStart(2, '0'),
      day: String(p.day).padStart(2, '0')
    };
  }
  const monthMatch = /^(\d{4})-(\d{1,2})$/.exec(s);
  if (monthMatch) {
    const y = parseInt(monthMatch[1], 10);
    const m = parseInt(monthMatch[2], 10);
    if (y >= 1 && y <= 9999 && m >= 1 && m <= 12) {
      return { year: String(y), month: String(m).padStart(2, '0'), day: '' };
    }
    return { year: '', month: '', day: '' };
  }
  const yearMatch = /^(\d{1,4})$/.exec(s);
  if (yearMatch) {
    const y = parseInt(yearMatch[1], 10);
    if (y >= 1 && y <= 9999) return { year: String(y), month: '', day: '' };
  }
  return { year: '', month: '', day: '' };
}

/**
 * Build YYYY-MM-DD from year, month, day strings (e.g. from part inputs).
 * Uses '01' for missing month/day. Returns '' if year is missing or invalid.
 */
export function buildDateFromParts(year: string, month: string, day: string): string {
  const y = year.trim();
  if (!y) return '';
  const yNum = parseInt(y, 10);
  if (yNum < 1 || yNum > 9999) return '';
  const m = month.trim() || '01';
  const d = day.trim() || '01';
  const mNum = parseInt(m, 10);
  const dNum = parseInt(d, 10);
  if (mNum < 1 || mNum > 12 || dNum < 1 || dNum > 31) return '';
  const mm = String(mNum).padStart(2, '0');
  const dd = String(dNum).padStart(2, '0');
  const candidate = `${yNum}-${mm}-${dd}`;
  return parseDateOnly(candidate) ? candidate : '';
}
